<template>
	<div class="dnb-principal-card">
		<div class="dnb-principal-card__avatar">
			<CoreIcon
				:name="confirmed ? 'account-check-outline' : 'account-search-outline'"
				:size="28"
				fillColor="var(--color-neutral-900)"
			/>
		</div>
		<div class="dnb-principal-card__main">
			<p :class="['dnb-principal-card__name', { 'dnb-principal-card__name--matching': nameMatches }]">
				{{ displayName }}
			</p>
			<div class="dnb-principal-card__meta">
				<div class="dnb-principal-card__meta-item">
					<span class="dnb-principal-card__meta-label">
						{{ $t('§eq_operator.auth_person_date_of_birth') }}
					</span>
					<span :class="['dnb-principal-card__meta-value', { 'dnb-principal-card__meta-value--matching': matchingFields.date_of_birth }]">
						{{ formattedBirthDate }}
					</span>
				</div>
				<div class="dnb-principal-card__meta-item">
					<span class="dnb-principal-card__meta-label">
						{{ $t('§eq_operator.auth_person_country') }}
					</span>
					<span :class="['dnb-principal-card__meta-value', { 'dnb-principal-card__meta-value--matching': matchingFields.country_id }]">
						{{ countryLabel || '—' }}
					</span>
				</div>
				<div v-if="jobTitle" class="dnb-principal-card__meta-item">
					<span class="dnb-principal-card__meta-label">
						{{ $t('§eq_operator.dnb_principal_card_job_title') }}
					</span>
					<span class="dnb-principal-card__meta-value">
						{{ jobTitle }}
					</span>
				</div>
			</div>
		</div>
		<Badge
			class="dnb-principal-card__badge"
			value="D&B"
			severity="info"
		/>
	</div>
</template>

<script>
import { parseISO } from 'date-fns'
import Badge from 'primevue/badge'
import CoreIcon from '@/components/core/CoreIcon.vue'

export default {
	name: 'DnbPrincipalCard',

	components: {
		Badge,
		CoreIcon,
	},

	props: {
		principal: {
			type: Object,
			required: true,
		},
		countryLabel: {
			type: String,
			default: '',
		},
		confirmed: {
			type: Boolean,
			default: false,
		},
		matchingFields: {
			type: Object,
			default: () => ({}),
		},
	},

	computed: {
		nameMatches() {
			return !!(this.matchingFields.first_name && this.matchingFields.last_name)
		},

		displayName() {
			const parts = [this.principal.givenName, this.principal.familyName]
				.filter(Boolean)
				.join(' ')
				.trim()
			return this.principal.fullName || parts || '—'
		},

		jobTitle() {
			const titles = Array.isArray(this.principal.jobTitles) ? this.principal.jobTitles : []
			return titles[0]?.title || ''
		},

		formattedBirthDate() {
			if (!this.principal.birthDate) return '—'
			try {
				return this.displayAsDate(parseISO(this.principal.birthDate))
			} catch {
				return '—'
			}
		},
	},
}
</script>

<style>
.dnb-principal-card {
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	padding: 1rem 1.25rem;
	border: 1px solid var(--color-neutral-700);
	border-radius: 8px;
	background-color: var(--color-white, #fff);
}

.dnb-principal-card__avatar {
	flex: 0 0 auto;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background-color: var(--color-neutral-500);
	display: flex;
	align-items: center;
	justify-content: center;
}

.dnb-principal-card__main {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
}

.dnb-principal-card__name {
	font-weight: 600;
	font-size: 1.6rem;
	color: var(--text-dark);
	margin: 0;
}

.dnb-principal-card__meta {
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}

.dnb-principal-card__meta-item {
	display: flex;
	flex-direction: column;
}

.dnb-principal-card__meta-label {
	color: var(--color-neutral-900);
	font-size: 1.2rem;
}

.dnb-principal-card__meta-value {
	color: var(--text-dark);
	font-size: 1.4rem;
	font-weight: 500;
}

.dnb-principal-card__name--matching {
	color: var(--color-primary);
}

.dnb-principal-card__meta-value--matching {
	color: var(--color-primary);
}

.dnb-principal-card__badge {
	flex: 0 0 auto;
	align-self: flex-start;
	font-weight: 600;
}
</style>
